<template>
  <div class="content">
    <h1 class="has-text-centered">OSI-Modell</h1>
    <div class="box">
      <p>Eine kompakte Zusammenfassung über das OSI-Modell.</p>
      <FAQsTableOfContents :faqs="faqs" />
    </div>

    <div class="box">
      <FAQs :faqs="faqs" :h_level="2" />
    </div>

    <FeedbackBox />
  </div>
</template>

<script>
import FeedbackBox from '@/components/FeedbackBox.vue'
import FAQs from '@/components/FAQs.vue'
import FAQsTableOfContents from '@/components/FAQsTableOfContents.vue'

export default {
  name: 'theory-osi-model',
  components: {
    FeedbackBox,
    FAQs,
    FAQsTableOfContents
  },
  metaInfo: {
    title: 'OSI-Modell | Elektrotechnik-Rechner.at',
    meta: [
      {
        name: 'description',
        content: 'Eine kompakte Zusammenfassung über das OSI-Modell.'
      }
    ]
  },
  created () {
    this.$store.commit('updateNavTitle', { title: '<span class="fas fa-angle-left"></span> Zurück', route: 'network-engineering' })
  },
  data () {
    return {
      faqs: [
        {
          question: 'Was ist das OSI-Modell?',
          answer:
            '<ul>' +
            '  <li>OSI = <strong>O</strong>pen <strong>S</strong>ystems <strong>I</strong>nterconnection</li>' +
            '  <li>Wurde von ISO (<strong>I</strong>nternational <strong>O</strong>rganization for <strong>S</strong>tandardization) entworfen</li>' +
            '  <li>Hat eine Schichtenarchitektur</li>' +
            '  <li>Referenzmodell für herstellerunabhängige Kommunikationssysteme</li>' +
            '  <li>Design-Grundlage für Kommunikationsprotokolle und Netzwerke</li>' +
            '  <li>Soll Kommunikation über unterschiedlichste technische Systeme hinweg ermöglichen</li>' +
            '  <li>Besteht aus sieben Schichten (layers) mit jeweils eng begrenzten Aufgaben</li>' +
            '  <li>Jede Schicht stellt der darüberliegenden Schicht Dienste zur Verfügung</li>' +
            '</ul>' +
            '<div class="has-text-centered">' +
            '  <img src="/img/theory/OSI-Modell.png">' +
            '</div>'
        },
        {
          question: 'Welche Schichten hat das OSI-Modell?',
          answer:
            '<ul>' +
            '  <li>Schicht 1: Bitübertragungsschicht (Physical Layer)</li>' +
            '  <li>Schicht 2: Sicherungsschicht (Data Link Layer)</li>' +
            '  <li>Schicht 3: Vermittlungsschicht (Network Layer)</li>' +
            '  <li>Schicht 4: Transportschicht (Transport Layer)</li>' +
            '  <li>Schicht 5: Sitzungsschicht (Session Layer)</li>' +
            '  <li>Schicht 6: Darstellungsschicht (Presentation Layer)</li>' +
            '  <li>Schicht 7: Anwendungsschicht (Application Layer)</li>' +
            '</ul>'
        },
        {
          question: 'Welche Aufgaben hat die Bitübertragungsschicht im OSI-Modell?',
          answer:
            '<ul>' +
            '  <li>Maßnahmen und Verfahren zur Übertragung von Bifolgen über leitungsgebundene oder leitungslose Übertragungsstrecken (elektrische oder optische Signale, elektromagnetische Wellen oder Schall)</li>' +
            '  <li>Hardware auf dieser Schicht:</li>' +
            '  <ul>' +
            '    <li>Repeater</li>' +
            '    <li>Hubs</li>' +
            '    <li>Leitungn</li>' +
            '    <li>Stecker</li>' +
            '  </ul>' +
            '</ul>'
        },
        {
          question: 'Welche Aufgaben hat die Sicherungsschicht im OSI-Modell?',
          answer:
            '<ul>' +
            '  <li>Logische Verbindungen mit Datenpaketen und elementare Fehlererkennungsmechanismen</li>' +
            '  <li>Physikalische Adressierung von Datenpaketen</li>' +
            '  <li>Soll für zuverlässige und funktionierende Verbindung sorgen</li>' +
            '  <li>Enthält Funktionen zur ...</li>' +
            '  <ul>' +
            '    <li>... Fehlererkennung</li>' +
            '    <li>... Fehlerbehebung</li>' +
            '    <li>... und Datenflusskontrolle</li>' +
            '  </ul>' +
            '  <li>Hardware auf dieser Schicht:</li>' +
            '  <ul>' +
            '    <li>Bridge</li>' +
            '    <li>Switch (Multiport-Bridge)</li>' +
            '  </ul>' +
            '</ul>'
        },
        {
          question: 'Welche Aufgaben hat die Vermittlungsschicht im OSI-Modell?',
          answer:
            '<ul>' +
            '  <li>Routing (Wegsuche) und Datenflusskontrolle</li>' +
            '  <li>Steuert zeitlich und logisch getrennte Kommunikation unabhängig vom Übertragungsmedium und der Topologie</li>' +
            '  <li>Logische Adressierung der Endgeräte</li>' +
            '  <li>Sorgt bei ...</li>' +
            '  <ul>' +
            '    <li>... leitungsorientierten Diensten für das Schalten von Leitungen</li>' +
            '    <li>... paketorientierten Diensten für die Weitervermittlung von Datenpaketen</li>' +
            '  </ul>' +
            '</ul>'
        },
        {
          question: 'Welche Aufgaben hat die Transportschicht im OSI-Modell?',
          answer:
            '<ul>' +
            '  <li>Logische Ende-zu-Ende-Verbindungen</li>' +
            '  <li>Bindeglied zwischen transportorientierten und anwendungsorientierten Schicht</li>' +
            '  <li>Datenpakete werden einer Anwendung zugeordnet</li>' +
            '</ul>'
        },
        {
          question: 'Welche Aufgaben hat die Sitzungsschicht im OSI-Modell?',
          answer:
            '<ul>' +
            '  <li>Steuerung logischer Verbindungen</li>' +
            '  <li>Sorgt für die Prozesskommunikation zwischen zwei Systemen</li>' +
            '  <li>Steuerungs- und Kontrollmechanismen für die Verbindung und den Datenaustausch</li>' +
            '</ul>'
        },
        {
          question: 'Welche Aufgaben hat die Darstellungsschicht im OSI-Modell?',
          answer:
            '<ul>' +
            '  <li>Ausgabe von Daten in Standardformate</li>' +
            '  <li>Wandelt Daten von oder zu der Anwendungsschicht in ein geeignetes Format um</li>' +
            '  <li>Datenkompression und Verschlüsselung gehören auch zu dieser Schicht</li>' +
            '</ul>'
        },
        {
          question: 'Welche Aufgaben hat die Anwendungsschicht im OSI-Modell?',
          answer:
            '<ul>' +
            '  <li>' +
            '    Dienste, Anwendungen und Netzmanagement' +
            '    <i>(Browser, eMail-Programm, ...)</i>' +
            '  </li>' +
            '  <li>Stellt Funktionen für Anwendungen zur Verfügung</li>' +
            '  <li>Dateneingabe und -ausgabe</li>' +
            '</ul>'
        }
      ]
    }
  }
}
</script>
